// Interior art — the room shell and its furniture, drawn from shapes like
// everything else in this game. Type-only engine import, matching render.ts.
//
// TWO RULES HOLD THIS TOGETHER.
//
// 1. ONE PERSPECTIVE. The game is top-down 3/4, so every solid indoors is built
//    from `boxTop` + a front face, or `discTop` + a rim for round things. No
//    flat plan-view lozenges and no face-on elevations mixed in — mixed angles
//    are exactly why untextured shapes read as placeholder art.
// 2. WARM. The outdoor stone and sand tones go chalky once they fill the whole
//    screen, so interiors use the CI palette (ochre earth, lime plaster, fired
//    clay, terracotta) and let the window wedge and the hearth do real work.
import type { Draw } from '../../engine/webgpu.js';
import { C, CI, INTERIOR } from '../config.js';
import * as R from '../render.js';
import type { Furniture, Interior } from '../interiors.js';

// ── the 3/4 primitives every solid is built from ─────────────────────────────

/** The top face of a box, foreshortened and slightly narrower at the back. */
function boxTop(d: Draw, x: number, y: number, w: number, td: number, col: string): void {
  const inset = Math.min(w * 0.05, 7);
  d.fill([
    { x: x + inset, y }, { x: x + w - inset, y },
    { x: x + w, y: y + td }, { x, y: y + td },
  ], col);
}

/** A box: lit top surface, darker front face, contact shadow. `depth` is how
 *  much of the height is top surface — the one knob that sets the angle. */
function box(d: Draw, f: { x: number; y: number; w: number; h: number },
             top: string, front: string, depth = 0.40): void {
  const td = f.h * depth;
  R.softShadow(d, f.x + f.w / 2, f.y + f.h, f.w * 0.5);
  d.rect(f.x, f.y + td, f.w, f.h - td, front);
  R.grain(d, f.x, f.y + td, f.w, f.h - td, R.shade(front, -34), 0.16, 22);
  boxTop(d, f.x, f.y, f.w, td, top);
  R.grain(d, f.x, f.y, f.w, td, R.shade(top, -30), 0.13, 24);
  d.line(f.x, f.y + td, f.x + f.w, f.y + td, 1.2, R.shade(front, -30), 0.55);
  d.rect(f.x, f.y + td * 0.1, f.w * 0.5, 2, C.goldLight, 0.16);
}

/** The top face of a cylinder: a circle seen at the room's angle. */
function discTop(d: Draw, cx: number, cy: number, rx: number, col: string, alpha = 1): void {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < 18; i++) {
    const a = (i / 18) * Math.PI * 2;
    pts.push({ x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * rx * 0.42 });
  }
  d.fill(pts, col, alpha);
}

/** A cylinder: foreshortened top, short rim below it. */
function cylinder(d: Draw, cx: number, yTop: number, rx: number, height: number,
                  top: string, side: string): void {
  R.softShadow(d, cx, yTop + rx * 0.42 + height, rx);
  d.rect(cx - rx, yTop, rx * 2, height, side);
  discTop(d, cx, yTop + height, rx, side);
  discTop(d, cx, yTop, rx, top);
}

// ── the room shell ───────────────────────────────────────────────────────────

/** The floor, the walls and the light falling into the room. */
export function room(d: Draw, it: Interior, dayT: number, t: number): void {
  const { x, y, w, h } = it.room;
  const W = INTERIOR.wall;
  const seed = x * 0.31 + y * 0.17;

  // packed earth, trodden and swept — warmer than the sand outside
  d.rect(x, y, w, h, CI.floor);
  R.mottle(d, x, y, w, h, CI.floorDark, 0.26, 130);
  R.mottle(d, x, y, w, h, CI.floorLit, 0.14, 210);
  R.grain(d, x, y, w, h, R.shade(CI.floorDark, -22), 0.12, 28);
  // worn pale where the household walks, between the door and the hearth
  R.dapple(d, x + w * 0.42, y + h * 0.66, w * 0.30, seed + 3, 0.10);
  R.dapple(d, x + w * 0.24, y + h * 0.48, w * 0.20, seed + 9, 0.08);

  // lime plaster walls: lit along the top, shaded where they meet the floor
  for (const r of [
    { x, y, w, h: W }, { x, y: y + h - W, w, h: W },
    { x, y, w: W, h }, { x: x + w - W, y, w: W, h },
  ]) {
    d.rect(r.x, r.y, r.w, r.h, CI.wall);
    R.mottle(d, r.x, r.y, r.w, r.h, CI.wallShade, 0.22, 90);
    R.grain(d, r.x, r.y, r.w, r.h, R.shade(CI.wallShade, -26), 0.10, 24);
  }
  d.rect(x, y, w, 5, CI.wallLit, 0.55);
  d.rect(x + W, y + W, w - W * 2, 5, CI.wallShade, 0.45);
  d.rect(x + W, y + W, 5, h - W * 2, CI.wallShade, 0.30);
  d.rect(x + w - W - 5, y + W, 5, h - W * 2, CI.wallShade, 0.30);

  // the doorway, and the daylight standing in it
  const dr = it.door;
  d.rect(dr.x - 5, dr.y - 5, dr.w + 10, dr.h + 10, R.shade(CI.wallShade, -26));
  d.rect(dr.x, dr.y, dr.w, dr.h, R.shade(CI.beam, -14));
  const lit = 0.32 + 0.4 * (1 - Math.min(1, Math.abs(dayT - 0.45) * 2.2));
  d.rect(dr.x, dr.y, dr.w, dr.h, C.goldLight, lit * 0.55);

  // the rush mat inside the door — the thing you step on to leave
  const m = it.mat;
  d.fill([
    { x: m.x + 5, y: m.y }, { x: m.x + m.w - 5, y: m.y },
    { x: m.x + m.w, y: m.y + m.h }, { x: m.x, y: m.y + m.h },
  ], CI.straw, 0.85);
  for (let i = 1; i < 5; i++) {
    d.line(m.x + 4, m.y + (m.h * i) / 5, m.x + m.w - 4, m.y + (m.h * i) / 5, 1.3, R.shade(CI.straw, -34), 0.55);
  }
  R.speckle(d, x + w / 2, y + h * 0.6, w * 0.42, h * 0.32, R.shade(CI.floorDark, -14), 16, seed, 0.20);
  void t;
}

/** The shaft of daylight from the high window — the strongest single thing in
 *  the room, and what the whole interior is lit around. */
export function windowLight(d: Draw, it: Interior, f: Furniture, dayT: number): void {
  const cx = f.x + f.w / 2;
  const floorY = it.room.y + it.room.h - INTERIOR.wall;
  const lean = (dayT - 0.5) * 210;
  const strength = Math.max(0.42, 1 - Math.abs(dayT - 0.42) * 1.4);
  if (strength <= 0.01) return;
  d.fill([
    { x: cx - f.w * 0.55, y: f.y + f.h },
    { x: cx + f.w * 0.55, y: f.y + f.h },
    { x: cx + f.w * 1.25 + lean, y: floorY },
    { x: cx - f.w * 1.15 + lean, y: floorY },
  ], C.lamp, 0.22 * strength);
  d.fill([
    { x: cx - f.w * 0.30, y: f.y + f.h },
    { x: cx + f.w * 0.30, y: f.y + f.h },
    { x: cx + f.w * 0.72 + lean, y: floorY },
    { x: cx - f.w * 0.62 + lean, y: floorY },
  ], '#ffe0a0', 0.26 * strength);
}

/** Hearth and lamps. A household fire is banked, never quite out, so there is
 *  always some warmth in here — it rises as the day goes down. */
export function interiorLight(d: Draw, it: Interior, dayT: number, t: number): void {
  const dusk = 0.34 + 0.66 * Math.max(0, Math.min(1, (dayT - 0.5) / 0.34));
  for (const f of it.furniture) {
    if (f.kind === 'hearth') {
      R.lampGlow(d, f.x + f.w / 2, f.y + f.h * 0.62, 74 * dusk, t);
      d.circle(f.x + f.w / 2, f.y + f.h * 0.62, 130 * dusk, C.lamp, 0.05 * dusk);
    }
    if (f.kind === 'niche') R.lampGlow(d, f.x + f.w / 2, f.y + f.h * 0.72, 40 * dusk, t);
  }
}

/** Roof beams overhead, drawn last. Without them the space reads as an open
 *  pen rather than somewhere with a roof on it. */
export function beams(d: Draw, f: Furniture): void {
  const n = 5;
  for (let i = 0; i < n; i++) {
    const bx = f.x + 30 + ((f.w - 60) * i) / (n - 1);
    // the shadow fades out fast — a beam does not stripe the whole floor
    d.rect(bx - 8, f.y, 16, f.h * 0.30, CI.beam, 0.055);
    d.rect(bx - 9, f.y, 18, 26, CI.beam, 0.88);
    d.rect(bx - 9, f.y, 18, 4, R.shade(CI.beam, 26), 0.9);
  }
  d.rect(f.x, f.y, f.w, 10, CI.beam, 0.5);
}

// ── furniture ────────────────────────────────────────────────────────────────

/** One piece of furniture, anchored top-left like every rect in this codebase. */
export function piece(d: Draw, f: Furniture, t: number, products: number): void {
  const seed = f.seed ?? f.x * 0.37 + f.y * 0.19;
  const baseY = f.y + f.h;
  switch (f.kind) {
    case 'window': {
      d.rect(f.x - 6, f.y - 5, f.w + 12, f.h + 12, R.shade(CI.wallShade, -28));
      d.rect(f.x, f.y, f.w, f.h, C.sky);
      d.rect(f.x, f.y, f.w, f.h * 0.45, R.shade(C.sky, 18));
      d.rect(f.x, f.y + f.h * 0.68, f.w, f.h * 0.32, C.leaf, 0.4);
      for (let i = 1; i < 3; i++) d.line(f.x + (f.w * i) / 3, f.y, f.x + (f.w * i) / 3, f.y + f.h, 2.2, CI.beam, 0.9);
      d.rect(f.x - 6, f.y + f.h + 3, f.w + 12, 6, C.timber);
      d.rect(f.x - 6, f.y + f.h + 3, f.w + 12, 2, C.goldLight, 0.4);
      break;
    }

    case 'hearth': {
      box(d, f, CI.brick, CI.brickDark, 0.44);
      const cx = f.x + f.w / 2, top = f.y + f.h * 0.30;
      R.speckle(d, cx, f.y + f.h * 0.6, f.w * 0.4, f.h * 0.3, CI.brickDark, 12, seed, 0.45);
      d.rect(f.x, f.y + f.h * 0.44, f.w, 4, R.shade(CI.brick, 20), 0.5);
      d.rect(f.x, f.y + f.h * 0.30, f.w, 6, '#2e1c12', 0.35);   // soot above the fire
      discTop(d, cx, top, f.w * 0.30, '#3a2418');
      discTop(d, cx, top, f.w * 0.22, '#c2521f');
      discTop(d, cx, top + 1, f.w * 0.13, C.lamp);
      const flick = 0.7 + 0.3 * Math.sin(t * 5 + seed);
      d.circle(cx, top - 4, f.w * 0.10 * flick, C.lamp, 0.55);
      d.circle(cx, top - 14, f.w * 0.19, '#3f3730');
      d.circle(cx, top - 17, f.w * 0.17, '#4b4239');
      discTop(d, cx, top - 24, f.w * 0.15, '#2b2621');
      d.rect(cx - f.w * 0.20, top - 26, f.w * 0.40, 3.5, '#574d43');
      break;
    }

    case 'mat': {
      // a straw pallet with bedding folded on it. Sized by its own w/h, so the
      // boy's is simply a smaller one of the same thing.
      const td = f.h * 0.46;
      R.softShadow(d, f.x + f.w / 2, baseY, f.w * 0.5);
      d.rect(f.x, f.y + td, f.w, f.h - td, R.shade(CI.straw, -34));
      boxTop(d, f.x, f.y, f.w, td, CI.straw);
      R.grain(d, f.x, f.y, f.w, f.h, R.shade(CI.straw, -46), 0.24, 15);
      const bx = f.x + f.w * 0.10, bw = f.w * 0.80;
      d.fill([
        { x: bx + 5, y: f.y + td * 0.34 }, { x: bx + bw - 5, y: f.y + td * 0.34 },
        { x: bx + bw, y: f.y + f.h * 0.80 }, { x: bx, y: f.y + f.h * 0.80 },
      ], CI.cloth, 0.88);
      d.rect(bx, f.y + f.h * 0.58, bw, f.h * 0.10, CI.clothDark, 0.6);
      R.grain(d, bx, f.y + td * 0.34, bw, f.h * 0.5, CI.clothDark, 0.30, 13);
      d.rect(bx + 4, f.y + td * 0.4, bw - 8, 3, CI.clothWorn, 0.5);
      cylinder(d, f.x + f.w * 0.5, f.y + 3, f.w * 0.20, 6, R.shade(CI.linen, 18), CI.linen);
      break;
    }

    case 'table': {
      box(d, f, C.timber, C.timberDark, 0.44);
      const cx = f.x + f.w / 2, topY = f.y + f.h * 0.22;
      d.rect(f.x + 10, baseY - 6, 9, 8, R.shade(C.timberDark, -18));
      d.rect(f.x + f.w - 19, baseY - 6, 9, 8, R.shade(C.timberDark, -18));
      discTop(d, cx - f.w * 0.16, topY, 15, R.shade(CI.straw, 16));
      d.circle(cx - f.w * 0.16, topY - 4, 12, R.shade(CI.straw, 22));
      discTop(d, cx + f.w * 0.10, topY + 4, 11, CI.olive);
      cylinder(d, cx + f.w * 0.28, topY - 6, 8, 11, R.shade(CI.glaze, 22), CI.glaze);
      break;
    }

    case 'cushion': {
      const td = f.h * 0.62;
      R.softShadow(d, f.x + f.w / 2, baseY, f.w * 0.46);
      d.rect(f.x + 3, f.y + td, f.w - 6, f.h - td, CI.clothDark);
      boxTop(d, f.x, f.y + 3, f.w, td, CI.cloth);
      d.circle(f.x + f.w * 0.5, f.y + td * 0.5 + 3, f.w * 0.30, CI.clothWorn, 0.45);
      R.grain(d, f.x, f.y, f.w, f.h, CI.clothDark, 0.28, 12);
      d.circle(f.x + f.w * 0.5, f.y + td * 0.55, 2.4, CI.clothDark, 0.8);
      break;
    }

    case 'rug': {
      d.rect(f.x, f.y, f.w, f.h, CI.clothDark, 0.30);
      d.rect(f.x + 12, f.y + 12, f.w - 24, f.h - 24, CI.olive, 0.20);
      for (let i = 1; i < 6; i++) {
        d.line(f.x + 16, f.y + (f.h * i) / 6, f.x + f.w - 16, f.y + (f.h * i) / 6, 1.5, CI.straw, 0.16);
      }
      R.grain(d, f.x, f.y, f.w, f.h, CI.clothDark, 0.12, 22);
      break;
    }

    case 'bench': {
      box(d, f, C.timber, C.timberDark, 0.38);
      d.rect(f.x + 8, baseY - 4, 8, 6, R.shade(C.timberDark, -20));
      d.rect(f.x + f.w - 16, baseY - 4, 8, 6, R.shade(C.timberDark, -20));
      break;
    }

    case 'chest': {
      box(d, f, C.timberLight, C.timberDark, 0.36);
      d.rect(f.x + 4, f.y + f.h * 0.36, f.w - 8, 5, R.shade(C.timberDark, 22));
      d.rect(f.x + f.w * 0.44, f.y + f.h * 0.30, f.w * 0.12, f.h * 0.22, C.gold, 0.9);
      d.rect(f.x + 6, f.y + f.h * 0.56, f.w - 12, 4, R.shade(C.timberDark, -20), 0.7);
      break;
    }

    case 'shelfWall': {
      d.rect(f.x, f.y + f.h * 0.52, f.w, f.h * 0.28, C.timberDark);
      d.rect(f.x, f.y + f.h * 0.42, f.w, f.h * 0.14, C.timberLight);
      R.grain(d, f.x, f.y + f.h * 0.42, f.w, f.h * 0.38, R.shade(C.timberDark, -26), 0.2, 20);
      d.rect(f.x + 10, f.y + f.h * 0.80, 8, f.h * 0.20, C.timberDark);
      d.rect(f.x + f.w - 18, f.y + f.h * 0.80, 8, f.h * 0.20, C.timberDark);
      for (let i = 0; i < 3; i++) {
        const px = f.x + f.w * (0.22 + i * 0.28);
        cylinder(d, px, f.y + f.h * 0.04, 13 - i * 2, f.h * 0.36,
                 R.shade(CI.glaze, 26 - i * 10), R.shade(CI.glaze, i * 8));
      }
      d.rect(f.x, f.y + f.h * 0.42, f.w, 2, C.goldLight, 0.35);
      break;
    }

    case 'jar': {
      const cx = f.x + f.w / 2;
      R.softShadow(d, cx, baseY, f.w * 0.5);
      {
        const belly: { x: number; y: number }[] = [];
        for (let i = 0; i <= 16; i++) {
          const a = Math.PI * (i / 16);
          belly.push({ x: cx + Math.cos(a) * f.w * 0.5, y: f.y + f.h * 0.56 - Math.sin(a) * f.h * 0.44 });
        }
        for (let i = 0; i <= 10; i++) {
          const a = Math.PI * (i / 10);
          belly.push({ x: cx - Math.cos(a) * f.w * 0.30, y: f.y + f.h * 0.56 + Math.sin(a) * f.h * 0.44 });
        }
        d.fill(belly, CI.glaze);
      }
      R.grain(d, f.x, f.y, f.w, f.h, R.shade(CI.glaze, -34), 0.2, 16);
      d.fill([
        { x: cx - f.w * 0.30, y: f.y + f.h * 0.22 }, { x: cx - f.w * 0.12, y: f.y + f.h * 0.22 },
        { x: cx - f.w * 0.16, y: f.y + f.h * 0.70 }, { x: cx - f.w * 0.34, y: f.y + f.h * 0.66 },
      ], R.shade(CI.glaze, 30), 0.5);
      discTop(d, cx, f.y + f.h * 0.12, f.w * 0.24, R.shade(CI.glaze, -24));
      d.rect(cx - f.w * 0.28, f.y + f.h * 0.05, f.w * 0.56, 5, R.shade(CI.glaze, 16));
      break;
    }

    case 'potsFloor': {
      for (let i = 0; i < 3; i++) {
        const px = f.x + f.w * (0.2 + i * 0.3);
        const rr = 13 - i * 2 + R.hash01(seed, i) * 4;
        cylinder(d, px, f.y + f.h * (0.2 + i * 0.12), rr, f.h * 0.42,
                 R.shade(CI.glaze, 24), R.shade(CI.glaze, -6 + i * 10));
      }
      break;
    }

    case 'waterstand': {
      d.rect(f.x + 8, f.y + f.h * 0.54, f.w - 16, f.h * 0.46, C.timberDark);
      R.softShadow(d, f.x + f.w / 2, baseY, f.w * 0.5);
      const cx = f.x + f.w / 2;
      d.fill([
        { x: cx - f.w * 0.26, y: f.y }, { x: cx + f.w * 0.26, y: f.y },
        { x: cx + f.w * 0.44, y: f.y + f.h * 0.30 }, { x: cx + f.w * 0.20, y: f.y + f.h * 0.60 },
        { x: cx - f.w * 0.20, y: f.y + f.h * 0.60 }, { x: cx - f.w * 0.44, y: f.y + f.h * 0.30 },
      ], R.shade(CI.glaze, -8));
      R.grain(d, f.x, f.y, f.w, f.h * 0.6, R.shade(CI.glaze, -34), 0.2, 15);
      discTop(d, cx, f.y + 2, f.w * 0.26, R.shade(CI.glaze, -26));
      discTop(d, cx, f.y + 3, f.w * 0.18, '#4a7f95');
      break;
    }

    case 'grindstone': {
      const cx = f.x + f.w / 2;
      d.rect(f.x + 6, f.y + f.h * 0.58, f.w - 12, f.h * 0.42, C.timberDark);
      cylinder(d, cx, f.y + f.h * 0.16, f.w * 0.42, f.h * 0.30, R.shade(CI.quern, 16), R.shade(CI.quern, -32));
      discTop(d, cx, f.y + f.h * 0.16, f.w * 0.12, R.shade(CI.quern, -46));
      d.rect(cx + f.w * 0.22, f.y + f.h * 0.02, 6, f.h * 0.22, C.timber);
      R.speckle(d, cx, f.y + f.h * 0.18, f.w * 0.3, f.h * 0.10, R.shade(CI.quern, -34), 8, seed, 0.35);
      break;
    }

    case 'firewood': {
      R.softShadow(d, f.x + f.w / 2, baseY, f.w * 0.5);
      for (let row = 0; row < 3; row++) {
        for (let i = 0; i < 3; i++) {
          const lx = f.x + 8 + i * (f.w / 3.4) + R.hash01(seed, row * 3 + i) * 5;
          const ly = f.y + f.h - 16 - row * (f.h * 0.26);
          cylinder(d, lx + 11, ly, 11, 8, R.shade(C.wood, 12 - row * 6), C.timber);
        }
      }
      break;
    }

    case 'sack': {
      const cx = f.x + f.w / 2;
      R.softShadow(d, cx, baseY, f.w * 0.5);
      d.fill([
        { x: cx - f.w * 0.20, y: f.y + f.h * 0.10 }, { x: cx + f.w * 0.20, y: f.y + f.h * 0.10 },
        { x: cx + f.w * 0.44, y: f.y + f.h * 0.36 }, { x: cx + f.w * 0.46, y: baseY },
        { x: cx - f.w * 0.46, y: baseY }, { x: cx - f.w * 0.44, y: f.y + f.h * 0.36 },
      ], CI.linen);
      R.grain(d, f.x, f.y, f.w, f.h, R.shade(CI.linen, -46), 0.30, 14);
      d.rect(cx - f.w * 0.16, f.y, f.w * 0.32, f.h * 0.18, R.shade(CI.linen, -22));
      d.line(cx - f.w * 0.2, f.y + f.h * 0.16, cx + f.w * 0.2, f.y + f.h * 0.16, 2, C.timber, 0.8);
      break;
    }

    case 'basket': {
      const cx = f.x + f.w / 2, td = f.h * 0.34;
      R.softShadow(d, cx, baseY, f.w * 0.48);
      d.fill([
        { x: f.x, y: f.y + td * 0.5 }, { x: f.x + f.w, y: f.y + td * 0.5 },
        { x: f.x + f.w * 0.82, y: baseY }, { x: f.x + f.w * 0.18, y: baseY },
      ], R.shade(C.wood, -14));
      for (let i = 1; i < 4; i++) {
        d.line(f.x + f.w * 0.06, f.y + td * 0.5 + (f.h * i) / 5, f.x + f.w * 0.94, f.y + td * 0.5 + (f.h * i) / 5, 1.4, R.shade(C.wood, -34), 0.6);
      }
      discTop(d, cx, f.y + td * 0.5, f.w * 0.5, R.shade(C.wood, 16));
      discTop(d, cx, f.y + td * 0.5, f.w * 0.42, R.shade(C.wood, -30));
      break;
    }

    case 'niche': {
      d.rect(f.x - 4, f.y - 4, f.w + 8, f.h + 8, R.shade(CI.wallShade, -30));
      d.rect(f.x, f.y, f.w, f.h, R.shade(CI.wallShade, -6));
      d.rect(f.x, f.y, f.w, f.h * 0.3, R.shade(CI.wallShade, -22));
      d.rect(f.x + 6, f.y + f.h * 0.72, f.w - 12, 7, C.timber);
      const lx = f.x + f.w / 2;
      d.fill([
        { x: lx - 14, y: f.y + f.h * 0.72 }, { x: lx + 14, y: f.y + f.h * 0.72 },
        { x: lx + 8, y: f.y + f.h * 0.56 }, { x: lx - 8, y: f.y + f.h * 0.56 },
      ], CI.glaze);
      d.circle(lx + 13, f.y + f.h * 0.60, 4.5, C.lamp, 0.95);
      d.circle(lx + 13, f.y + f.h * 0.58, 9, C.lamp, 0.35);
      break;
    }

    case 'hangings': {
      for (let i = 0; i < 3; i++) {
        const hx = f.x + 20 + i * (f.w / 3);
        d.line(hx, f.y, hx, f.y + 12, 1.5, C.timberDark, 0.8);
        if (i === 1) { for (let k = -1; k <= 1; k++) d.line(hx, f.y + 12, hx + k * 5, f.y + 40, 3, CI.olive, 0.9); }
        else cylinder(d, hx, f.y + 12, 12, 18, R.shade(CI.glaze, 20 - i * 12), R.shade(CI.glaze, -4));
      }
      break;
    }

    case 'herbs': {
      for (let i = 0; i < 3; i++) R.herbBundle(d, f.x + 10 + i * 16, f.y, 1.0, seed + i);
      break;
    }

    case 'loom': R.loomProp(d, f.x + f.w / 2, baseY, f.w / 30, seed); break;

    case 'toy': {
      d.rect(f.x, f.y + 6, f.w, 8, C.wood);
      d.circle(f.x + 4, f.y + 4, 4, C.wood);
      d.line(f.x + 3, f.y + 14, f.x + 3, f.y + 18, 2, R.shade(C.wood, -20));
      d.line(f.x + f.w - 4, f.y + 14, f.x + f.w - 4, f.y + 18, 2, R.shade(C.wood, -20));
      break;
    }

    // ── the workshop store's own vocabulary ─────────────────────────────────
    case 'boards': {
      R.softShadow(d, f.x + f.w / 2, baseY, f.w * 0.5);
      for (let i = 0; i < 4; i++) {
        const bx = f.x + 4 + i * 12;
        d.line(bx, baseY, bx + R.hash01(seed, i) * 10 - 5, f.y, 9, i % 2 ? C.wood : R.shade(C.wood, -16));
      }
      break;
    }
    case 'shelf': {
      box(d, f, C.timberLight, C.timberDark, 0.3);
      for (let i = 0; i < 2; i++) R.plank(d, f.x + 4, f.y + 8 + i * (f.h * 0.44), f.w - 8, 6, C.wood, seed + i);
      break;
    }
    case 'toolwall': {
      d.rect(f.x, f.y + f.h - 6, f.w, 5, C.timberDark);
      for (let i = 0; i < 5; i++) {
        const tx = f.x + 14 + ((f.w - 28) * i) / 4;
        const kind = i % 3;
        if (kind === 0) { d.line(tx, f.y + 8, tx, f.y + f.h - 8, 2.6, C.rockDark); d.rect(tx - 3, f.y + f.h - 14, 6, 8, C.timber); }
        else if (kind === 1) { d.fill([{ x: tx - 7, y: f.y + 10 }, { x: tx + 7, y: f.y + 14 }, { x: tx + 5, y: f.y + 22 }, { x: tx - 7, y: f.y + 18 }], '#cfc9b0'); d.rect(tx - 2, f.y + 22, 5, 12, C.timber); }
        else { d.rect(tx - 2, f.y + 10, 4, 20, C.timberDark); d.circle(tx, f.y + 32, 4, C.gold, 0.85); }
      }
      break;
    }
    case 'rack': {
      R.softShadow(d, f.x + f.w / 2, baseY, f.w * 0.46);
      d.rect(f.x, f.y, 7, f.h, C.timberDark);
      d.rect(f.x + f.w - 7, f.y, 7, f.h, C.timberDark);
      for (let i = 0; i < 4; i++) R.plank(d, f.x + 2, f.y + 14 + i * ((f.h - 24) / 4), f.w - 4, 8, i % 2 ? C.wood : R.shade(C.wood, -14), seed + i);
      break;
    }
    case 'barrel': cylinder(d, f.x + f.w / 2, f.y, f.w * 0.5, f.h * 0.8, R.shade(C.timber, 18), C.timber); break;
    case 'benchTop': {
      box(d, f, C.timberLight, C.timberDark, 0.3);
      const n = Math.max(0, Math.min(4, products));
      for (let i = 0; i < n; i++) {
        box(d, { x: f.x + 14 + i * 46, y: f.y - 26, w: 34, h: 30 }, C.wood, R.shade(C.wood, -26), 0.4);
      }
      break;
    }
    case 'stool': box(d, f, C.timber, C.timberDark, 0.34); break;
    default: break;
  }
}
